import { useState, useEffect } from 'react';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapTenderOpeningList } from '../utils/tenderOpeningHelpers';
import { TENDER_QUICK_FILTERS } from '../utils/tenderOpeningListExport';
import { buildTenderOpeningListUrl } from './useTenderOpeningList';

// Open-tender counts per deadline bucket (due soon / overdue) for one
// category. Uses the list endpoint with PageSize=1 and reads totalItems.
const useTenderOpeningInsights = ({
  procurementTypeHeader,
  refreshTrigger,
  enabled = true,
}) => {
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!enabled || !procurementTypeHeader || !token || !isAuthenticated()) {
      return undefined;
    }

    const controller = new AbortController();
    setInsights(null);

    Promise.all(
      TENDER_QUICK_FILTERS.map(async filter => {
        const url = buildTenderOpeningListUrl({
          currentPage: 1,
          rowsPerPage: 1,
          procurementTypeHeader,
          deadlineStatus: filter.params.DeadlineStatus,
        });
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const { totalItems } = unwrapTenderOpeningList(await response.json());
        return [filter.key, totalItems];
      })
    )
      .then(entries => setInsights(Object.fromEntries(entries)))
      .catch(error => {
        // The banner is a hint only; the table reports real load failures.
        if (error.name !== 'AbortError') setInsights(null);
      });

    return () => controller.abort();
  }, [procurementTypeHeader, refreshTrigger, enabled]);

  return insights;
};

export default useTenderOpeningInsights;
